import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { useAuth } from 'auth';

import {
  fetchMySubscription,
  type SubscriptionEntitlementKey,
  type SubscriptionOverview,
} from './subscription.api';

const SUBSCRIPTION_QUERY_ENABLED =
  process.env.REACT_APP_SUBSCRIPTION_QUERY_ENABLED === 'true';

interface SubscriptionAccessContextValue {
  currentPlanKey: 'free' | 'pro';
  getIntegerEntitlementValue: (key: SubscriptionEntitlementKey) => number | null;
  hasEntitlement: (
    key: SubscriptionEntitlementKey,
    options?: { minimumIntegerValue?: number; requiredBooleanValue?: boolean }
  ) => boolean;
  isSubscriptionLoading: boolean;
  subscription: SubscriptionOverview | null;
}

const SubscriptionAccessContext = createContext<
  SubscriptionAccessContextValue | undefined
>(undefined);

const FALLBACK_SUBSCRIPTION: SubscriptionOverview = {
  plan: {
    key: 'free',
    name: 'Free',
    rank: 0,
  },
  subscription: null,
  entitlements: [
    {
      key: 'advanced_analytics',
      booleanValue: false,
      source: 'PLAN',
    },
    {
      key: 'max_published_positions',
      integerValue: 3,
      source: 'PLAN',
    },
    {
      key: 'priority_matching',
      booleanValue: false,
      source: 'PLAN',
    },
  ],
};

export function SubscriptionAccessProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { accessToken, isAuthenticated } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionOverview | null>(null);
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !accessToken || !SUBSCRIPTION_QUERY_ENABLED) {
      setSubscription(null);
      setIsSubscriptionLoading(false);
      return;
    }

    let isCancelled = false;
    setIsSubscriptionLoading(true);

    void fetchMySubscription(accessToken)
      .then((nextSubscription) => {
        if (!isCancelled) {
          setSubscription(nextSubscription);
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setSubscription(FALLBACK_SUBSCRIPTION);
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsSubscriptionLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [accessToken, isAuthenticated]);

  const value = useMemo<SubscriptionAccessContextValue>(() => {
    const resolvedSubscription = subscription ?? FALLBACK_SUBSCRIPTION;

    return {
      subscription,
      isSubscriptionLoading,
      currentPlanKey: resolvedSubscription.plan.key,
      getIntegerEntitlementValue(key) {
        const entitlement = resolvedSubscription.entitlements.find(
          (candidate) => candidate.key === key
        );

        return entitlement?.integerValue ?? null;
      },
      hasEntitlement(key, options) {
        const entitlement = resolvedSubscription.entitlements.find(
          (candidate) => candidate.key === key
        );

        if (options?.minimumIntegerValue !== undefined) {
          return (
            typeof entitlement?.integerValue === 'number' &&
            entitlement.integerValue >= options.minimumIntegerValue
          );
        }

        return (entitlement?.booleanValue ?? false) === (options?.requiredBooleanValue ?? true);
      },
    };
  }, [isSubscriptionLoading, subscription]);

  return (
    <SubscriptionAccessContext.Provider value={value}>
      {children}
    </SubscriptionAccessContext.Provider>
  );
}

export function useSubscriptionAccess() {
  const context = useContext(SubscriptionAccessContext);

  if (!context) {
    throw new Error(
      'useSubscriptionAccess must be used within a SubscriptionAccessProvider.'
    );
  }

  return context;
}
